import type { V2AdminCreditDetailDto, V2StartCreditDto } from '@idc-forms/api-client'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { creditsAdminV2Service } from '../services/credits-admin-v2.service'

/**
 * Abrir el expediente a mano — el camino manual de R10, del lado de la pantalla.
 *
 * ── POR QUÉ NO VIVE EN `useCreditExpedientMutations` ─────────────────────────
 *
 * Aquél se construye con un `creditId`, y acá el expediente **todavía no existe**: lo que hay es
 * una negociación cerrada. Meterlo ahí obligaría a inventar un id vacío para el único caso que no
 * tiene ninguno.
 *
 * ── ☠️ LOS EFECTOS VIVEN ACÁ, NO EN EL CALLBACK DE `mutate` ──────────────────
 *
 * React Query v5 **descarta los callbacks que se le pasan a `mutate()` si el componente que llamó
 * se desmontó antes de que la mutación termine**; los declarados en el `useMutation` corren
 * siempre. Con el botón en una celda de la tabla eso mordía de lleno —la celda desaparecía como
 * consecuencia de su propia acción— y el síntoma medido el 2026-08-24 fue el peor posible: el
 * expediente se creaba y en pantalla no pasaba absolutamente nada.
 *
 * Hoy el botón vive en el panel, que NO se desmonta al triunfar (sólo cambia de contenido), así que
 * la trampa ya no aplica. El toast se queda igual acá: es un efecto de la mutación, no del botón, y
 * dejarlo colgado del componente lo volvería a atar a que siga vivo.
 *
 * ── LA URL NO SE TOCA ────────────────────────────────────────────────────────
 *
 * El panel se direcciona por la FILA (el `neg_`), que existe antes y después del alta. Cuando el
 * listado se refresca, la fila pasa a traer su `credit` y el panel se convierte solo en la ficha
 * del expediente. La versión anterior escribía un `creditId` desde acá para abrirlo — un paso que
 * el direccionamiento correcto vuelve innecesario.
 *
 * ── LA RESPUESTA SIEMBRA EL DETALLE ──────────────────────────────────────────
 *
 * El `POST` contesta con el expediente completo —el mismo presentador que el `GET`—, así que se
 * escribe directo en la caché del detalle. Sin eso, abrir el panel dispararía un segundo viaje para
 * traer algo que ya vino, y se vería un esqueleto después de un alta ya hecha.
 *
 * ⚠️ El listado se INVALIDA en vez de parchearse: la fila cambia de estado, y con ella el conteo de
 * cada lente y —según el orden vigente— su posición. Reescribir eso a mano es cómo la tabla y los
 * tabs terminan diciendo cosas distintas.
 *
 * ── ⚠️ NO DECLARA `onError` ──────────────────────────────────────────────────
 *
 * El toast de error lo pone `defaultOptions.mutations.onError` del QueryClient (`app.tsx`). Como es
 * un DEFAULT y no un callback del `MutationCache`, declarar un `onError` propio acá lo REEMPLAZA y
 * el error se iría en silencio. Los códigos que este alta puede devolver —negociación no cerrada,
 * expediente ya existente, plantilla sin publicar— tienen su texto en `ERROR_MESSAGES`.
 */
export function useStartCreditProcess() {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: V2StartCreditDto) =>
      await creditsAdminV2Service.start(accessToken, input),
    onSuccess: (detail: V2AdminCreditDetailDto) => {
      queryClient.setQueryData(queryKeys.credits.detailV2(detail.id), detail)
      toast.success(`Expediente ${detail.code} abierto`)
      void queryClient.invalidateQueries({ queryKey: queryKeys.credits.adminListV2() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.credits.summaryV2() })
    }
  })
}
